import { useCallback, useState } from "react";

import {
    copyMenuItem,
    createMenuCategory,
    createMenuItem,
    deleteMenuCategory,
    deleteMenuItem,
    moveMenuItem,
    reorderMenuCategories,
    reorderMenuItems,
    updateMenuCategory,
} from "services/menu/rest";
import { MenuItemListDto } from "services/menu/restModels";
import {
    CopyMenuItemRequestDto,
    CreateMenuCategoryRequestDto,
    CreateMenuItemRequestDto,
    UpdateMenuCategoryRequestDto,
} from "services/menu/restRequests";

import { UseNotificationSnackbarReturn } from "shared/ui";

export interface UseMenuEditorMutationsParams {
    menuId: number;
    notify: UseNotificationSnackbarReturn;
    // Структурные изменения меняют состав и счётчики сайдбара — его перезапрашиваем.
    refreshCategories: () => void;
    reloadItems: () => void;
    removeItemFromList: (menuItemId: number) => void;
}

export interface MenuEditorMutations {
    saving: boolean;
    createCategory: (name: Record<string, string>) => Promise<boolean>;
    updateCategory: (
        menuCategoryId: number,
        body: UpdateMenuCategoryRequestDto
    ) => Promise<boolean>;
    deleteCategory: (menuCategoryId: number) => Promise<boolean>;
    reorderCategories: (menuCategoryIds: number[]) => Promise<boolean>;
    createItem: (body: CreateMenuItemRequestDto) => Promise<MenuItemListDto | null>;
    deleteItem: (menuItemId: number) => Promise<boolean>;
    moveItem: (menuItemId: number, menuCategoryId: number) => Promise<boolean>;
    copyItem: (menuItemId: number, body: CopyMenuItemRequestDto) => Promise<boolean>;
    reorderItems: (menuCategoryId: number, menuItemIds: number[]) => Promise<boolean>;
}

// Расписание и время приготовления новой категории оставляем пустыми — их правят
// в «Настройках категории», а форма создания спрашивает только название.
const emptyDay = { isEnabled: false, slots: [{ fromHour: 0, fromMinute: 0, toHour: 23, toMinute: 59 }] };

const EMPTY_SCHEDULE: CreateMenuCategoryRequestDto["schedule"] = {
    isEnabled: false,
    monday: emptyDay,
    tuesday: emptyDay,
    wednesday: emptyDay,
    thursday: emptyDay,
    friday: emptyDay,
    saturday: emptyDay,
    sunday: emptyDay,
};

export const useMenuEditorMutations = (
    params: UseMenuEditorMutationsParams
): MenuEditorMutations => {
    const { menuId, notify, refreshCategories, reloadItems, removeItemFromList } = params;

    const [saving, setSaving] = useState<boolean>(false);

    /*
    * Единая обёртка мутации: флаг сохранения, тост об успехе и разбор ошибки в одном месте.
    * REST-слой на неуспехе кидает Error с русским текстом от бэка — его и показываем.
    * */
    const run = useCallback(
        async <T>(
            action: () => Promise<T>,
            successMessage: string,
            errorMessage: string
        ): Promise<T | null> => {
            setSaving(true);

            try {
                const result = await action();
                notify.showSuccess(successMessage);
                return result;
            } catch (error) {
                notify.showError(errorMessage, error);
                return null;
            } finally {
                setSaving(false);
            }
        },
        [notify]
    );

    const createCategory = useCallback(
        async (name: Record<string, string>) => {
            const result = await run(
                () =>
                    createMenuCategory({
                        menuId,
                        name,
                        schedule: EMPTY_SCHEDULE,
                        cookingTime: { fromTime: null, toTime: null },
                        isInStopList: false,
                    }),
                "Категория создана",
                "Не удалось создать категорию"
            );

            if (result) refreshCategories();

            return !!result;
        },
        [menuId, refreshCategories, run]
    );

    const updateCategory = useCallback(
        async (menuCategoryId: number, body: UpdateMenuCategoryRequestDto) => {
            const result = await run(
                () => updateMenuCategory(menuCategoryId, body),
                "Категория сохранена",
                "Не удалось сохранить категорию"
            );

            if (result) {
                refreshCategories();
                // Стоп-лист категории влияет на подписи позиций — список тоже освежаем.
                reloadItems();
            }

            return !!result;
        },
        [refreshCategories, reloadItems, run]
    );

    const deleteCategory = useCallback(
        async (menuCategoryId: number) => {
            const result = await run(
                () => deleteMenuCategory(menuCategoryId),
                "Категория удалена",
                "Не удалось удалить категорию"
            );

            if (result) refreshCategories();

            return !!result;
        },
        [refreshCategories, run]
    );

    const reorderCategories = useCallback(
        async (menuCategoryIds: number[]) => {
            const result = await run(
                () => reorderMenuCategories({ menuId, menuCategoryIds }),
                "Порядок категорий сохранён",
                "Не удалось сохранить порядок категорий"
            );

            if (result) refreshCategories();

            return !!result;
        },
        [menuId, refreshCategories, run]
    );

    const createItem = useCallback(
        async (body: CreateMenuItemRequestDto) => {
            const result = await run(
                () => createMenuItem(body),
                "Позиция создана",
                "Не удалось создать позицию"
            );

            if (result) {
                reloadItems();
                refreshCategories();
            }

            return result;
        },
        [refreshCategories, reloadItems, run]
    );

    const deleteItem = useCallback(
        async (menuItemId: number) => {
            const result = await run(
                () => deleteMenuItem(menuItemId),
                "Позиция удалена",
                "Не удалось удалить позицию"
            );

            if (result) {
                // Убираем из списка точечно, без перезапроса: страница остаётся на месте.
                removeItemFromList(menuItemId);
                refreshCategories();
            }

            return !!result;
        },
        [refreshCategories, removeItemFromList, run]
    );

    const moveItem = useCallback(
        async (menuItemId: number, menuCategoryId: number) => {
            const result = await run(
                () => moveMenuItem(menuItemId, { menuCategoryId }),
                "Позиция перенесена",
                "Не удалось перенести позицию"
            );

            if (result) {
                removeItemFromList(menuItemId);
                refreshCategories();
            }

            return !!result;
        },
        [refreshCategories, removeItemFromList, run]
    );

    const copyItem = useCallback(
        async (menuItemId: number, body: CopyMenuItemRequestDto) => {
            const result = await run(
                () => copyMenuItem(menuItemId, body),
                "Позиция скопирована",
                "Не удалось скопировать позицию"
            );

            if (result) {
                // Копия попадает в текущий список, только если её положили в эту же категорию.
                if (body.targetMenuId === menuId) {
                    reloadItems();
                    refreshCategories();
                }
            }

            return !!result;
        },
        [menuId, refreshCategories, reloadItems, run]
    );

    const reorderItems = useCallback(
        async (menuCategoryId: number, menuItemIds: number[]) => {
            const result = await run(
                () => reorderMenuItems({ menuId, menuCategoryId, menuItemIds }),
                "Порядок позиций сохранён",
                "Не удалось сохранить порядок позиций"
            );

            if (result) reloadItems();

            return !!result;
        },
        [menuId, reloadItems, run]
    );

    return {
        saving,
        createCategory,
        updateCategory,
        deleteCategory,
        reorderCategories,
        createItem,
        deleteItem,
        moveItem,
        copyItem,
        reorderItems,
    };
};
